import type {
  Order,
  OrderItem,
  Product,
  Variant,
} from '@scayle/storefront-nuxt'
import {
  getFirstAttributeValue,
  getTotalAppliedReductions,
} from '@scayle/storefront-nuxt'

import usePromotionTrackingLocalStorage from '../usePromotionTrackingLocalStorage'
import useProductTrackingLocalStorage from '../useProductTrackingLocalStorage'
import useCheckoutTrackingLocalStorage from '../useCheckoutTrackingLocalStorage'
import { getCategoryLevels } from '~/utils/tracking'
import type { TrackingEvent, TrackingPayload } from '~/types/tracking'
import useLocalStorage from '#layers/base/composables/useLocalStorage'
import { getSizeLabel } from '#layers/base/types/variant'

/**
 * Groups order items by variant ID and calculates quantities efficiently.
 *
 * @param {OrderItem[]} orderItems - The order items to group.
 * @returns {Array<{ item: OrderItem<Product, Variant>; quantity: number }>} - Array of unique items with their quantities.
 */
const getUniqueItemsWithQuantity = (
  orderItems: OrderItem[],
): Array<{ item: OrderItem<Product, Variant>; quantity: number }> => {
  const itemVariantMap = new Map()

  orderItems?.forEach((item) => {
    const variantId = item.variant.id
    if (!itemVariantMap.has(variantId)) {
      itemVariantMap.set(variantId, { item, quantity: 1 })
    } else {
      itemVariantMap.get(variantId).quantity++
    }
  })
  return Array.from(itemVariantMap.values())
}

/**
 * Get the coupon code for an order item
 * @param order - The order data
 * @param price - The price of the order item
 * @returns The coupon code
 */
const getOrderItemCoupon = (order: Order, price?: OrderItem['price']) => {
  const hasVoucher = price?.appliedReductions?.some(
    ({ category }) => category === 'voucher',
  )
  return hasVoucher ? (order.vouchers?.[0]?.code?.toUpperCase() ?? '') : ''
}

const getTaxWithoutDeliveryCosts = (order: Order) => {
  const deliveryFee = order.cost.appliedFees?.find(
    ({ category }) => category === 'delivery',
  )
  const deliveryFeeVatAmount = deliveryFee?.tax?.vat?.amount ?? 0
  return (order.cost.tax.vat?.amount ?? 0) - deliveryFeeVatAmount
}

const { stored: trackedOrders, set: setTrackedOrders } = useLocalStorage<
  number[]
>('trackedOrders', [])

const usePurchaseEvents = (
  track: (event: TrackingEvent, payload: TrackingPayload) => any,
  _options: any,
) => {
  const { getUserPayload } = useTrackingUserPayload()

  const { deleteProductPromotions } = usePromotionTrackingLocalStorage()

  const {
    deleteProductTrackingItems,
    getStoredProductTrackingItem,
    getStoredProductTrackingItemByMasterKey,
  } = useProductTrackingLocalStorage()

  const { getStoredCheckoutTrackingItem, deleteCheckoutTrackingItems } =
    useCheckoutTrackingLocalStorage()

  const currentShop = useCurrentShop()

  return {
    trackPurchaseEvent: async (orderData: Order) => {
      if (trackedOrders.value.includes(orderData?.id)) {
        return
      }

      const payment = orderData.payment?.[0]
      const orderItemsWithQuantity = getUniqueItemsWithQuantity(
        orderData?.items ?? [],
      )
      const storedCheckoutTracking = getStoredCheckoutTrackingItem(
        orderData?.basketKey,
      )

      track('purchase', {
        user: await getUserPayload(orderData?.customer?.email),
        checkout_type: storedCheckoutTracking?.checkout_type,
        newsletter_signup: storedCheckoutTracking?.newsletter_signup,
        ecommerce: {
          tax: toFloat(getTaxWithoutDeliveryCosts(orderData)),
          shipping: toFloat(orderData.shipping?.deliveryCosts ?? 0),
          transaction_id: orderData?.id,
          coupon: orderData.vouchers?.[0]?.code?.toUpperCase() ?? '',
          currency: currentShop.value?.currency,
          value: toFloat(
            orderItemsWithQuantity.reduce(
              (acc, { item, quantity }) => acc + item.price.withTax * quantity,
              0,
            ),
          ),
          net_value: toFloat(
            orderItemsWithQuantity.reduce(
              (acc, { item, quantity }) =>
                acc + item.price.withoutTax * quantity,
              0,
            ),
          ),
          payment_type: payment?.key,

          items: orderItemsWithQuantity.map(({ item, quantity }, index) => {
            const { product, variant, price } = item
            const categoryLevels = getCategoryLevels(product.categories)
            const appliedReductions = price.appliedReductions ?? []
            const storedProductTrackingByRef = getStoredProductTrackingItem(
              String(product.referenceKey),
            )
            const storedProductTrackingByMasterKey =
              getStoredProductTrackingItemByMasterKey(String(product.masterKey))
            const storedProductTracking =
              storedProductTrackingByRef || storedProductTrackingByMasterKey

            return {
              item_id: product.id.toString(),
              item_sku: variant.referenceKey || '',
              item_name:
                getFirstAttributeValue(product.attributes, 'name')?.label || '',
              item_brand:
                getFirstAttributeValue(product.attributes, 'brand')?.label ||
                '',
              item_variant:
                getFirstAttributeValue(product.attributes, 'color')?.label ||
                '',
              item_size: getSizeLabel(variant),
              item_size_key: variant.referenceKey
                ? getSizeKey(variant.referenceKey) || ''
                : '',
              item_category: categoryLevels.item_category || '',
              item_category2: categoryLevels.item_category2 || '',
              item_category3: categoryLevels.item_category3 || '',
              coupon: getOrderItemCoupon(orderData, price),
              price: toFloat(price.withTax),
              quantity,
              stock: variant.stock.quantity,
              index: index + 1,
              ordertype: 'shop',
              discount: toFloat(
                getTotalAppliedReductions({ appliedReductions })
                  .absoluteWithTax,
              ),
              item_list_name: storedProductTracking?.item_list_name || '',
              item_list_type: storedProductTracking?.item_list_type || '',
            }
          }),
        },
      })

      setTrackedOrders([...trackedOrders.value, orderData.id])

      const { ids, refs } = orderItemsWithQuantity.reduce(
        (acc, { item }) => {
          const idRef = item.variant.referenceKey?.split('-')[0]
          acc.ids.push(item.product.id.toString())
          if (idRef) {
            acc.refs.push(idRef)
          }
          return acc
        },
        { ids: [], refs: [] } as { ids: string[]; refs: string[] },
      )

      // cleanup local storage
      deleteProductPromotions(ids)
      deleteProductTrackingItems(refs)
      deleteCheckoutTrackingItems([orderData.basketKey])
    },
  }
}

export default usePurchaseEvents
